import { Injectable } from '@angular/core';
import { BreadcrumbData, CustomRouteData, PageData } from '@app/custom-route.types';
import { TranslateService } from '@ngx-translate/core';
import { Client as IframeClient } from 'iframe-coordinator';
import { EnvData } from 'iframe-coordinator/dist/messages/Lifecycle';
import { cloneDeep } from 'lodash';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';
import { EnvironmentHelperService } from './environment-helper.service';
import { FeatureService } from './feature.service';

/** Iframe Coordinator Client wrapper service - handles navigation, breadcrumbs, and toasts */
@Injectable({
  providedIn: 'root'
})
export class IframeCoordinatorClientService {
  private iframeClient: IframeClient;
  private readonly iframeEnvData$: Subject<EnvData> = new ReplaySubject(1);

  constructor(
    private readonly envHelper: EnvironmentHelperService,
    private readonly feature: FeatureService,
    private readonly translate: TranslateService
  ) {}

  /**
   * Determines if client and host applications are communicating.
   * Methods that depend on environmentData will throw errors if it is undefined.
   */
  public get isActive(): boolean {
    return !!this.client.environmentData;
  }

  /**
   * Gets the current instance of iframe client
   */
  public get client(): IframeClient {
    return this.iframeClient;
  }

  /**
   * Gets the complete environment data
   */
  public get envData$(): Observable<EnvData> {
    return this.iframeEnvData$.pipe();
  }

  private get hasAlreadyInit(): boolean {
    return !!this.iframeClient;
  }

  /**
   * Initializes iframe service
   */
  public init(): Observable<EnvData> {
    if (!this.envHelper.isIframed) {
      return null;
    }

    if (this.hasAlreadyInit) {
      return null;
    }

    this.iframeClient = this.initializeIframeClient();

    this.setupIframeEnvListener();
    this.iframeClient.start();

    return this.feature.hasFeature('adminUI.v3.IFC.migration').pipe(
      switchMap((hasIfc) => (hasIfc ? this.envData$ : of(null))),
      first()
    );
  }

  /**
   * Request navigation from the host app.
   * By default the path will be updated from client relative to full url.
   *
   * Returns true on successful navigation, otherwise false
   */
  public navigate(path: string, { transform = 'clientRelative' }: NavigateOptions = {}): boolean {
    if (!this.iframeClient) {
      return false;
    }
    let url: string;

    try {
      switch (transform) {
        case 'clientRelative':
          url = this.iframeClient.urlFromClientPath(path);
          break;

        case 'hostRelative':
          url = this.iframeClient.urlFromHostPath(path);
          break;

        case 'none':
          url = path;
          break;

        // no default
      }

      this.iframeClient.requestNavigation({ url });
    } catch {
      return false;
    }

    return true;
  }

  /**
   * Send page data to the host application. This can be used to update title, help documentation, and breadcrumbs.
   */
  public updatePageData(data: CustomRouteData): boolean {
    if (!this.iframeClient || !data.page) {
      return false;
    }

    try {
      const payload = this.processPageData(data);
      this.iframeClient.publish({ topic: 'page.data', payload });
    } catch {
      return false;
    }

    return true;
  }

  /**
   * Processes route data into page data. Uses route data context to evaluate breadcrumbs.
   */
  private processPageData(data: CustomRouteData): PageData {
    const output = cloneDeep(data.page);

    output.title = this.processPageTitle(data.page.title);

    // Process breadcrumb text
    if (data.page?.breadcrumbs) {
      output.breadcrumbs.segments = this.processBreadcrumbs(data);
    }

    return output;
  }

  /** Localizes the page title */
  private processPageTitle(title: string): string {
    return this.translate.instant(title);
  }

  /**
   * Evaluate dynamic breadcrumb text or localize static localization key for each segment.
   * Uses the route data context for evaluating dynamic breadcrumbs.
   */
  private processBreadcrumbs(data: CustomRouteData): BreadcrumbData[] {
    return data.page.breadcrumbs.segments.map((segment) => ({
      ...segment,
      text:
        segment.text instanceof Function ? segment.text(data) : this.translate.instant(segment.text)
    }));
  }

  /**
   * Create a new instance of the client
   */
  private initializeIframeClient(): IframeClient {
    return new IframeClient({
      hostOrigin: this.envHelper.hostOrigin
    });
  }

  /**
   * Listener for environment data from host app
   */
  private setupIframeEnvListener(): void {
    this.iframeClient.addListener('environmentalData', (envData: EnvData) => {
      this.iframeEnvData$.next(envData);
    });
  }
}

interface NavigateOptions {
  transform?: PathTransform;
}

type PathTransform = 'hostRelative' | 'clientRelative' | 'none';
